<script setup>
import {
  computed,
  onMounted,
  nextTick,
  onUnmounted,
  useTemplateRef,
  inject,
} from 'vue';
import { useWindowSize, useElementBounding, useScrollLock } from '@vueuse/core';
import { vOnClickOutside } from '@vueuse/components';

import TeleportWithDirection from 'dashboard/components-next/TeleportWithDirection.vue';

const props = defineProps({
  x: { type: Number, default: 0 },
  y: { type: Number, default: 0 },
});

const emit = defineEmits(['close']);

const elementToLock = inject('contextMenuElementTarget', null);

const menuRef = useTemplateRef('menuRef');

const scrollLockElement = computed(() => {
  if (!elementToLock?.value) return null;
  return elementToLock.value?.$el;
});

const isLocked = useScrollLock(scrollLockElement);

const { width: windowWidth, height: windowHeight } = useWindowSize();
const { width: menuWidth, height: menuHeight } = useElementBounding(menuRef);

const calculatePosition = (x, y, menuW, menuH, windowW, windowH) => {
  const PADDING = 16;
  // Initial position
  let left = x;
  let top = y;
  // Boundary checks
  const isOverflowingRight = left + menuW > windowW - PADDING;
  const isOverflowingBottom = top + menuH > windowH - PADDING;
  // Adjust position if overflowing
  if (isOverflowingRight) left = windowW - menuW - PADDING;
  if (isOverflowingBottom) top = windowH - menuH - PADDING;
  return {
    left: Math.max(PADDING, left),
    top: Math.max(PADDING, top),
  };
};

const position = computed(() => {
  if (!menuRef.value) return { top: `${props.y}px`, left: `${props.x}px` };

  const { left, top } = calculatePosition(
    props.x,
    props.y,
    menuWidth.value,
    menuHeight.value,
    windowWidth.value,
    windowHeight.value
  );

  return {
    top: `${top}px`,
    left: `${left}px`,
  };
});

onMounted(() => {
  isLocked.value = true;
  nextTick(() => menuRef.value?.focus());
});

const handleClose = () => {
  isLocked.value = false;
  emit('close');
};

// advup — achado 2026-07-21 (Victor: usuário mobile ficava PRESO no menu de
// contexto da conversa depois de atribuir etiqueta, só conseguia sair
// fechando o app). Fechar só por `@blur` funciona no desktop (clique em
// qualquer lugar tira o foco do menu), mas toque numa `<div>` comum não move
// foco nenhum nos navegadores mobile — blur nunca disparava. `v-on-click-
// outside` (mesmo padrão já usado no Sidebar.vue) detecta via pointerdown,
// que funciona com toque e mouse igual.

// 🔴 O MENU FECHAVA ANTES DO CLIQUE (17/09/2026, relato do Dr. Henrique:
// "tá bugado para transferir", e nenhuma opção do menu da conversa
// funcionava — não lida, etiqueta, time). Os itens passaram a receber foco
// (`tabindex`, para teclado e leitor de tela). Ao apertar um item, o foco ia
// para ELE, a caixa disparava `blur` e fechava — o item sumia antes do
// `click`. Só fecha quando o foco sai para FORA do menu.
const aoPerderFoco = evento => {
  if (menuRef.value?.contains(evento.relatedTarget)) return;
  handleClose();
};

onUnmounted(() => {
  isLocked.value = false;
});
</script>

<template>
  <TeleportWithDirection to="body">
    <div
      ref="menuRef"
      v-on-click-outside="handleClose"
      class="fixed outline-none z-[9999] cursor-pointer"
      :style="position"
      tabindex="0"
      @focusout="aoPerderFoco"
    >
      <slot />
    </div>
  </TeleportWithDirection>
</template>
